// Static demo-mode fill-in data for RegularizationRequests.component.jsx
// (the manager-facing "regularization requests awaiting my approval" view).
// Shapes must match what RegularizationRequestsComponent's `regList` /
// `teams` (state.team, only `.projects` needs overriding) / `memberDetails`
// expect — see RegularizationRequests.config.jsx's `columnsConfig` cell
// renderers, which resolve `row.projectId` against `teams.projects`
// ({id, data:{project_name}}), `row.assignee` against `memberDetails`, and
// the reporter via getRegularizationReporterLabel(row, memberDetails)
// (prefers `row.userName`, matching the real doc shape written by
// RegularizationModal.component.jsx).

// Firestore Timestamp-like stub — RegularizationRequests.config.jsx's
// `formatAsDdMmYyyy` (via `parseDateValue`) supports a `.toDate()` function.
const toTimestamp = (isoString) => ({
  toDate: () => new Date(isoString),
});

// Anchored to "today" so the request/report dates never go stale (see
// UserAttendance.demoData.js for the same pattern).
const DAY_MS = 24 * 60 * 60 * 1000;
const daysFromNow = (n) => new Date(Date.now() + n * DAY_MS);
const isoAt = (daysOffset, hh, mm) => {
  const d = daysFromNow(daysOffset);
  d.setUTCHours(hh, mm, 0, 0);
  return d.toISOString();
};

// Consumed by RegularizationRequests.config.jsx's `columnsConfig`
// cellRenderers: project (via projectId → teams.projects), taskName,
// taskHours/taskMinutes, startTime (task date), createdAt/requestDate
// (report date), teamName (department), assignee (→ memberDetails),
// reporter (userName, falling back to memberDetails[userId]), status.
// Leans toward "Pending" since this is the manager approval queue.
export const DEMO_MANAGER_REGULARIZATION_REQUESTS = [
  {
    id: 'demo-mgr-reg-1',
    userId: 'demo-emp-1',
    userName: 'Rachel Turner',
    projectId: 'demo-proj-1',
    taskID: 'demo-task-1',
    taskName: 'Homepage layout fixes',
    taskDetails: 'Forgot to clock in before starting work',
    taskHours: 3,
    taskMinutes: 30,
    assignee: 'demo-manager-2',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    status: 'Pending',
    startTime: toTimestamp(isoAt(-2, 9, 0)),
    createdAt: toTimestamp(isoAt(-2, 18, 0)),
    requestDate: toTimestamp(isoAt(-2, 18, 0)),
  },
  {
    id: 'demo-mgr-reg-2',
    userId: 'demo-emp-2',
    userName: 'Michael Chen',
    projectId: 'demo-proj-2',
    taskID: 'demo-task-2',
    taskName: 'API integration debugging',
    taskDetails: 'Worked from a client site without the desktop app',
    taskHours: 4,
    taskMinutes: 0,
    assignee: 'demo-manager-1',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    status: 'Pending',
    startTime: toTimestamp(isoAt(-4, 10, 0)),
    createdAt: toTimestamp(isoAt(-4, 17, 0)),
    requestDate: toTimestamp(isoAt(-4, 17, 0)),
  },
  {
    id: 'demo-mgr-reg-3',
    userId: 'demo-emp-3',
    userName: 'Sarah Johnson',
    projectId: 'demo-proj-3',
    taskID: 'demo-task-3',
    taskName: 'Dashboard analytics fix',
    taskDetails: 'Tracker crashed mid-session',
    taskHours: 2,
    taskMinutes: 15,
    assignee: 'demo-manager-1',
    teamId: 'demo-team-sales',
    teamName: 'Sales',
    status: 'Approved',
    startTime: toTimestamp(isoAt(-6, 9, 30)),
    createdAt: toTimestamp(isoAt(-6, 16, 0)),
    requestDate: toTimestamp(isoAt(-6, 16, 0)),
  },
  {
    id: 'demo-mgr-reg-4',
    userId: 'demo-emp-4',
    userName: 'David Kim',
    projectId: 'demo-proj-1',
    taskID: 'demo-task-4',
    taskName: 'Missed punch - client call overran',
    taskDetails: 'Client call ran past clock-out, forgot to resume tracking',
    taskHours: 1,
    taskMinutes: 30,
    assignee: 'demo-manager-1',
    teamId: 'demo-team-support',
    teamName: 'Customer Support',
    status: 'Declined',
    startTime: toTimestamp(isoAt(-8, 14, 0)),
    createdAt: toTimestamp(isoAt(-8, 20, 0)),
    requestDate: toTimestamp(isoAt(-8, 20, 0)),
  },
  {
    id: 'demo-mgr-reg-5',
    userId: 'demo-emp-1',
    userName: 'Rachel Turner',
    projectId: 'demo-proj-2',
    taskID: 'demo-task-5',
    taskName: 'Sprint planning prep',
    taskDetails: 'Prepared docs on personal laptop over the weekend',
    taskHours: 2,
    taskMinutes: 30,
    assignee: 'demo-manager-2',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    status: 'Pending',
    startTime: toTimestamp(isoAt(-1, 9, 0)),
    createdAt: toTimestamp(isoAt(-1, 15, 0)),
    requestDate: toTimestamp(isoAt(-1, 15, 0)),
  },
  {
    id: 'demo-mgr-reg-6',
    userId: 'demo-emp-2',
    userName: 'Michael Chen',
    projectId: 'demo-proj-3',
    taskID: 'demo-task-6',
    taskName: 'Bug triage session',
    taskDetails: 'Joined an ad-hoc triage call untracked',
    taskHours: 1,
    taskMinutes: 45,
    assignee: 'demo-manager-1',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    status: 'Pending',
    startTime: toTimestamp(isoAt(0, 11, 0)),
    createdAt: toTimestamp(isoAt(0, 14, 0)),
    requestDate: toTimestamp(isoAt(0, 14, 0)),
  },
];

// Consumed by RegularizationRequests.config.jsx's "Project" column
// (`teams.projects.find((p) => p.id === row.projectId)?.data?.project_name`)
// — spliced into `teams.projects` for display (see component override).
export const DEMO_TEAMS_PROJECTS = [
  {
    id: 'demo-proj-1',
    data: { project_name: 'Website Redesign', deleted: false },
  },
  { id: 'demo-proj-2', data: { project_name: 'Mobile App', deleted: false } },
  {
    id: 'demo-proj-3',
    data: { project_name: 'Internal Tools', deleted: false },
  },
];

// Consumed by RegularizationRequests.config.jsx's "Assignee" column
// (`memberDetails?.[row.assignee]` → userName || userEmailid). Reporter names
// come from `row.userName` directly (see getRegularizationReporterLabel), so
// only the viewing manager's own uid needs an entry here.
export const DEMO_MEMBER_DETAILS = {
  'demo-manager-1': {
    userName: 'Rachel Turner',
    userEmailid: 'rachel.turner@meridianlabs.com',
    userType: 'Manager',
  },
  'demo-manager-2': {
    userName: 'Emma Williams',
    userEmailid: 'emma.williams@meridianlabs.com',
    userType: 'Manager',
  },
};
